
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, Users, Shield, Calendar } from "lucide-react";

const AboutEn = () => {
  const { t } = useTranslation(['common']);

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">About uplivion</h1>
            <p className="text-xl text-gray-600">Your life story deserves to be preserved.</p>
          </div>

          <div className="prose max-w-none">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
              <div className="flex items-center">
                <Calendar className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Beta Version</h3>
                  <p className="text-blue-800">
                    uplivion is currently in beta testing phase. We are continuously working 
                    to create the best platform for your personal memories.
                  </p>
                </div>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
              <p className="mb-4 text-lg">
                uplivion was created to help people document, organize, and share their life story 
                with the most important people in their lives. We believe that every life story 
                is unique and valuable.
              </p>
              <p className="mb-4">
                In a time when memories are often scattered across various apps and platforms, 
                uplivion provides a central place to document your life chronologically and meaningfully.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-6">What makes uplivion special?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Heart className="w-8 h-8 text-uplivion-orange flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Personal & Meaningful</h3>
                    <p className="text-sm">
                      Organize your memories by life phases and give each 
                      moment the attention it deserves.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Users className="w-8 h-8 text-uplivion-blue flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Connected with Friends</h3>
                    <p className="text-sm">
                      Share selected memories with friends and family 
                      while maintaining control over your privacy.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Shield className="w-8 h-8 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Secure & Private</h3>
                    <p className="text-sm">
                      Your data belongs to you. We use state-of-the-art security technologies 
                      and respect your privacy.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Calendar className="w-8 h-8 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Structured & Organized</h3>
                    <p className="text-sm">
                      From childhood to today - organize your life in meaningful 
                      sections with our innovative timeline system.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Our Approach</h2>
              <p className="mb-4">
                uplivion is based on the belief that technology should strengthen human connections, 
                not replace them. That's why we focus on:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Simplicity:</strong> Intuitive operation without complicated features</li>
                <li><strong>Privacy:</strong> Your data remains under your control</li>
                <li><strong>Meaning:</strong> Focus on important moments, not superficial content</li>
                <li><strong>Longevity:</strong> Built for preservation over years</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Beta Phase: Your Participation is Needed</h2>
              <p className="mb-4">
                As a beta user, you are part of the uplivion family and help us develop 
                the best possible platform. Your feedback and suggestions flow directly 
                into further development.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">What does beta mean for you?</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Free use of all basic functions</li>
                  <li>• Direct influence on new features</li>
                  <li>• Regular updates and improvements</li>
                  <li>• Personal support for questions</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Contact & Support</h2>
              <p className="mb-4">
                Do you have questions, suggestions, or need help? We are here for you:
              </p>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Arc Rider Ventures GmbH</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Jänickendorfer Str. 64a<br/>
                    14943 Brandenburg<br/>
                    Germany
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    Local Court Berlin Charlottenburg HRB199081B<br/>
                    Managing Director: Jakob Jordan
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">General Inquiries</h4>
                    <p className="text-sm text-gray-600">support@uplivion.life</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Technical Support</h4>
                    <p className="text-sm text-gray-600">support@uplivion.life</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Feedback & Ideas</h4>
                    <p className="text-sm text-gray-600">support@uplivion.life</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Data Protection</h4>
                    <p className="text-sm text-gray-600">support@uplivion.life</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild>
                <Link to="/">Back to Home</Link>
              </Button>
              <Button variant="outline" asChild>
                <a href="https://app.uplivion.life/register">Try Beta Now</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutEn;
